(() => {
  'use strict';

  const DESKTOP_QUERY = '(min-width: 990px)';
  const MOBILE_SLIDER_QUERY = '(max-width: 749px)';
  const VALID_SIDEBAR_TYPES = new Set(['collection', 'article', 'tool', 'service', 'brand', 'products', 'cta']);
  const controllers = new WeakMap();
  let mobileSliderId = 0;

  const slugify = (value, maxLength = 80) => {
    const replacements = { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss' };
    const normalized = String(value || '')
      .trim()
      .toLocaleLowerCase('de-DE')
      .replace(/[äöüß]/g, (character) => replacements[character])
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, maxLength)
      .replace(/-+$/g, '');

    return normalized || 'abschnitt';
  };

  const isValidId = (value) => typeof value === 'string' && value.length > 0 && !/\s/.test(value);

  const createUniqueId = (base, usedIds) => {
    const normalizedBase = slugify(base);
    let candidate = normalizedBase;
    let suffix = 2;

    while (usedIds.has(candidate)) {
      candidate = `${normalizedBase}-${suffix}`;
      suffix += 1;
    }

    usedIds.add(candidate);
    return candidate;
  };

  const buildHeadingPlan = (headingData, depth = 2, reservedIds = new Set()) => {
    const usedIds = new Set(reservedIds);
    const plan = [];
    let h2Count = 0;
    let hasParentH2 = false;

    headingData.forEach((heading) => {
      const level = Number(heading.level);
      if (level !== 2 && level !== 3) return;
      if (level === 3 && (depth !== 3 || !hasParentH2)) return;

      let id = isValidId(heading.id) && !usedIds.has(heading.id) ? heading.id : '';
      if (id) usedIds.add(id);
      else id = createUniqueId(heading.text, usedIds);

      if (level === 2) {
        h2Count += 1;
        hasParentH2 = true;
        plan.push({ ...heading, id, level, number: String(h2Count).padStart(2, '0') });
      } else {
        plan.push({ ...heading, id, level, number: '' });
      }
    });

    return { h2Count, items: plan };
  };

  const buildExternalVideoUrl = (provider, videoId) => {
    const normalizedProvider = String(provider || '').trim().toLowerCase();
    const normalizedId = String(videoId || '').trim();

    if ((normalizedProvider === 'youtube' || normalizedProvider === 'youtube-nocookie') && /^[a-zA-Z0-9_-]{6,20}$/.test(normalizedId)) {
      return `https://www.youtube-nocookie.com/embed/${encodeURIComponent(normalizedId)}?autoplay=1&rel=0`;
    }

    if (normalizedProvider === 'vimeo' && /^\d{5,20}$/.test(normalizedId)) {
      return `https://player.vimeo.com/video/${encodeURIComponent(normalizedId)}?autoplay=1&dnt=1`;
    }

    return '';
  };

  const publicApi = Object.freeze({
    buildExternalVideoUrl,
    buildHeadingPlan,
    createUniqueId,
    isValidId,
    slugify,
  });
  if (typeof window !== 'undefined') window.OfenerlebnisArticleRedesign = publicApi;
  if (typeof document === 'undefined') return;

  class ArticleRedesignController {
    constructor(root) {
      this.root = root;
      this.body = root.querySelector('[data-article-editorial-body]');
      this.core = root.querySelector('[data-article-core]');
      this.sidebar = root.querySelector('[data-article-sidebar]');
      this.toc = root.querySelector('[data-article-toc]');
      this.tocList = root.querySelector('[data-article-toc-list]');
      this.tocToggle = root.querySelector('[data-article-toc-toggle]');
      this.tocPanel = root.querySelector('[data-article-toc-panel]');
      this.desktopMedia = window.matchMedia(DESKTOP_QUERY);
      this.mobileSliderMedia = window.matchMedia(MOBILE_SLIDER_QUERY);
      this.cardPlaceholders = new Map();
      this.desktopSidebarCards = new Set();
      this.sidebarAssignments = [];
      this.mobileSliders = [];
      this.externalVideoTriggers = [];
      this.observer = null;
      this.mutationObserver = null;
      this.rebuildTimer = null;
      this.headings = [];

      if (!this.body) return;
      this.root.classList.add('is-enhanced');
      this.bindEvents();
      this.removeEmptyParagraphs();
      this.enhanceTables();
      this.rebuildToc();
      this.markDesktopSidebarCards();
      this.initializeMobileSliders();
      this.initializeExternalVideos();
      this.syncResponsiveState();
      this.observeBodyChanges();
    }

    bindEvents() {
      this.onResize = () => {
        this.syncResponsiveState();
      };
      this.onToggle = () => this.setTocExpanded(this.tocToggle?.getAttribute('aria-expanded') !== 'true');
      this.onTocClick = (event) => {
        const link = event.target.closest?.('[data-toc-target]');
        if (link && this.tocList?.contains(link)) this.setActiveHeading(link.dataset.tocTarget);
      };

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
      this.desktopMedia.addEventListener?.('change', this.onResize);
      this.mobileSliderMedia.addEventListener?.('change', this.onResize);
      this.tocToggle?.addEventListener('click', this.onToggle);
      this.tocList?.addEventListener('click', this.onTocClick);

      this.body.querySelectorAll('img').forEach((image) => {
        if (!image.complete) image.addEventListener('load', this.onResize, { once: true });
      });
    }

    removeEmptyParagraphs() {
      this.body.querySelectorAll(':scope > p').forEach((paragraph) => {
        const hasEmbeddedContent = paragraph.querySelector('img, picture, video, audio, iframe, table, svg');
        if (!hasEmbeddedContent && paragraph.textContent.replace(/\u00a0/g, ' ').trim() === '') paragraph.remove();
      });
    }

    enhanceTables() {
      this.body.querySelectorAll('table').forEach((table, index) => {
        if (table.parentElement?.classList.contains('oe-article-table-scroll')) return;
        const wrapper = document.createElement('div');
        wrapper.className = 'oe-article-table-scroll';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', `Tabelle ${index + 1}: horizontal scrollbar`);
        table.before(wrapper);
        wrapper.append(table);
      });
    }

    getReservedIds(processedHeadings) {
      const bodyHeadings = new Set(processedHeadings);
      const reserved = new Set();
      document.querySelectorAll('[id]').forEach((element) => {
        if (!bodyHeadings.has(element) && element.id) reserved.add(element.id);
      });
      return reserved;
    }

    rebuildToc() {
      if (!this.toc || !this.tocList || this.root.dataset.tocEnabled !== 'true') {
        this.hideToc();
        return;
      }

      const depth = Number(this.toc.dataset.tocDepth) === 3 ? 3 : 2;
      const headingElements = Array.from(this.body.querySelectorAll(depth === 3 ? 'h2, h3' : 'h2'));
      const faqHeading = this.root.querySelector('.oe-article-faq .oe-article-module__header h2');
      if (faqHeading) headingElements.push(faqHeading);
      const headingData = headingElements.map((element) => ({
        element,
        id: element.id,
        level: Number(element.tagName.slice(1)),
        text: element.textContent.trim(),
      }));
      const plan = buildHeadingPlan(headingData, depth, this.getReservedIds(headingElements));

      if (plan.h2Count < 2) {
        this.hideToc();
        return;
      }

      this.tocList.replaceChildren();
      this.headings = [];
      let currentChildren = null;

      plan.items.forEach((item) => {
        item.element.id = item.id;
        this.headings.push(item.element);
        const listItem = document.createElement('li');
        listItem.className = `oe-article-toc__item oe-article-toc__item--h${item.level}`;
        const link = document.createElement('a');
        link.className = 'oe-article-toc__link';
        link.href = `#${encodeURIComponent(item.id)}`;
        link.dataset.tocTarget = item.id;

        if (item.level === 2) {
          const number = document.createElement('span');
          number.className = 'oe-article-toc__number';
          number.setAttribute('aria-hidden', 'true');
          number.textContent = item.number;
          link.append(number, document.createTextNode(item.text));
          listItem.append(link);
          this.tocList.append(listItem);
          currentChildren = document.createElement('ol');
          currentChildren.className = 'oe-article-toc__children';
          listItem.append(currentChildren);
        } else if (currentChildren) {
          link.textContent = item.text;
          listItem.append(link);
          currentChildren.append(listItem);
        }
      });

      this.toc.hidden = false;
      this.observeActiveHeadings();
      this.setActiveHeading(this.headings[0]?.id);
      this.syncSidebarVisibility();
    }

    hideToc() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      this.headings = [];
      if (this.toc) this.toc.hidden = true;
      if (this.tocList) this.tocList.replaceChildren();
      this.syncSidebarVisibility();
    }

    observeActiveHeadings() {
      if (this.observer) this.observer.disconnect();
      if (!('IntersectionObserver' in window) || this.headings.length === 0) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top);
          if (visible[0]) this.setActiveHeading(visible[0].target.id);
        },
        { rootMargin: '-15% 0px -70% 0px', threshold: 0 }
      );
      this.headings.forEach((heading) => this.observer.observe(heading));
    }

    setActiveHeading(id) {
      if (!id || !this.tocList) return;
      this.tocList.querySelectorAll('[data-toc-target]').forEach((link) => {
        if (link.dataset.tocTarget === id) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    setTocExpanded(expanded) {
      if (!this.tocToggle || !this.tocPanel || this.toc?.hidden) return;
      this.tocToggle.setAttribute('aria-expanded', String(expanded));
      this.tocPanel.hidden = !expanded;
    }

    syncResponsiveState() {
      if (this.desktopMedia.matches) {
        this.setTocExpanded(true);
        this.moveSidebarCards();
      } else {
        this.setTocExpanded(false);
        this.restoreSidebarCards();
      }
      this.syncModules();
      this.syncSidebarVisibility();
      this.syncMobileSliders();
    }

    getSidebarSelections() {
      return [
        ['primary', this.root.dataset.sidebarPrimary],
        ['secondary', this.root.dataset.sidebarSecondary],
      ];
    }

    markDesktopSidebarCards() {
      const selectedTypes = new Set();

      this.getSidebarSelections().forEach(([slotName, type]) => {
        if (!VALID_SIDEBAR_TYPES.has(type) || selectedTypes.has(type)) return;
        selectedTypes.add(type);
        const card = Array.from(this.root.querySelectorAll(`[data-sidebar-candidate="${type}"]`)).find(
          (candidate) => !this.desktopSidebarCards.has(candidate)
        );
        if (!card) return;
        card.setAttribute('data-desktop-sidebar-only', '');
        this.desktopSidebarCards.add(card);
        this.sidebarAssignments.push({ card, slotName });
      });
    }

    moveSidebarCards() {
      this.sidebarAssignments.forEach(({ card, slotName }) => {
        const slot = this.root.querySelector(`[data-article-sidebar-slot="${slotName}"]`);
        if (!slot || !card) return;

        if (!this.cardPlaceholders.has(card)) {
          const placeholder = document.createComment(`article-sidebar-${slotName}`);
          card.before(placeholder);
          this.cardPlaceholders.set(card, placeholder);
        }
        slot.append(card);
      });
    }

    restoreSidebarCards() {
      this.cardPlaceholders.forEach((placeholder, card) => {
        if (placeholder.parentNode) placeholder.before(card);
      });
    }

    syncModules() {
      const hideDesktopSidebarCards = this.mobileSliderMedia.matches;
      this.root.querySelectorAll('[data-article-module]').forEach((module) => {
        const hasVisibleCard = Array.from(module.querySelectorAll('[data-article-card]')).some(
          (card) => !hideDesktopSidebarCards || !card.hasAttribute('data-desktop-sidebar-only')
        );
        module.hidden = !hasVisibleCard;
      });
    }

    initializeMobileSliders() {
      const modules = Array.from(
        this.root.querySelectorAll('[data-article-mobile-slider], [data-reference-related], [data-reference-products]')
      );

      modules.forEach((module) => {
        const track = module.querySelector('[data-article-mobile-slider-track], .oe-article-card-grid');
        const header = module.querySelector('.oe-article-module__header');
        const cards = track ? Array.from(track.querySelectorAll(':scope > [data-article-card]')) : [];
        if (!track || !header || cards.length === 0) return;

        const kind = module.dataset.articleMobileSlider || (module.hasAttribute('data-reference-related') ? 'related' : 'products');
        const label = header.querySelector('.oe-article-module__title, h2, h3')?.textContent.trim() || module.getAttribute('aria-label') || '';
        const trackId = track.id || `ArticleMobileSlider-${++mobileSliderId}`;
        track.id = trackId;
        module.classList.add('oe-article-mobile-slider');
        track.classList.add('oe-article-mobile-slider__track');

        let controls = module.querySelector('.oe-article-mobile-slider__controls');
        const controlsCreated = !controls;
        if (!controls) {
          controls = document.createElement('div');
          controls.className = 'oe-article-mobile-slider__controls';
          controls.hidden = true;

          const previous = document.createElement('button');
          previous.className = 'oe-article-mobile-slider__button';
          previous.type = 'button';
          previous.setAttribute('aria-controls', trackId);
          previous.setAttribute('aria-label', `Vorherige Karte: ${label}`);
          const previousIcon = document.createElement('span');
          previousIcon.setAttribute('aria-hidden', 'true');
          previousIcon.textContent = '\u2190';
          previous.append(previousIcon);

          const next = document.createElement('button');
          next.className = 'oe-article-mobile-slider__button';
          next.type = 'button';
          next.setAttribute('aria-controls', trackId);
          next.setAttribute('aria-label', `N\u00e4chste Karte: ${label}`);
          const nextIcon = document.createElement('span');
          nextIcon.setAttribute('aria-hidden', 'true');
          nextIcon.textContent = '\u2192';
          next.append(nextIcon);

          const status = document.createElement('span');
          status.className = 'oe-article-mobile-slider__status';
          status.setAttribute('aria-live', 'polite');
          status.setAttribute('aria-atomic', 'true');
          controls.append(previous, next, status);
          header.append(controls);
        }

        const previous = controls.querySelector('button:first-of-type');
        const next = controls.querySelector('button:nth-of-type(2)');
        const status = controls.querySelector('.oe-article-mobile-slider__status');
        if (!previous || !next || !status) return;

        const desktopEnabled = kind === 'related' || module.hasAttribute('data-article-desktop-slider');
        const slider = { cards, controls, controlsCreated, desktopEnabled, kind, label, module, next, previous, status, track };
        slider.onPrevious = () => this.moveMobileSlider(slider, -1);
        slider.onNext = () => this.moveMobileSlider(slider, 1);
        slider.onScroll = () => this.requestMobileSliderUpdate(slider);
        slider.onKeydown = (event) => {
          if ((!this.mobileSliderMedia.matches && !desktopEnabled) || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
          event.preventDefault();
          this.moveMobileSlider(slider, event.key === 'ArrowLeft' ? -1 : 1);
        };
        previous.addEventListener('click', slider.onPrevious);
        next.addEventListener('click', slider.onNext);
        track.addEventListener('scroll', slider.onScroll, { passive: true });
        track.addEventListener('keydown', slider.onKeydown);
        this.mobileSliders.push(slider);
      });
    }

    getMobileSliderIndex(slider) {
      const firstOffset = slider.cards[0]?.offsetLeft || 0;
      const closestIndex = slider.cards.reduce((currentClosest, card, index) => {
        const distance = Math.abs(card.offsetLeft - firstOffset - slider.track.scrollLeft);
        const closestDistance = Math.abs(
          slider.cards[currentClosest].offsetLeft - firstOffset - slider.track.scrollLeft
        );
        return distance < closestDistance ? index : currentClosest;
      }, 0);
      return Math.min(closestIndex, this.getMobileSliderMaxIndex(slider));
    }

    getMobileSliderMaxIndex(slider) {
      const visibleCount = slider.desktopEnabled && !this.mobileSliderMedia.matches
        ? (this.desktopMedia.matches ? 3 : 2)
        : 1;
      return Math.max(0, slider.cards.length - visibleCount);
    }

    moveMobileSlider(slider, direction) {
      const currentIndex = this.getMobileSliderIndex(slider);
      const targetIndex = Math.min(this.getMobileSliderMaxIndex(slider), Math.max(0, currentIndex + direction));
      const firstOffset = slider.cards[0]?.offsetLeft || 0;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      slider.track.scrollTo({
        behavior: reducedMotion ? 'auto' : 'smooth',
        left: slider.cards[targetIndex].offsetLeft - firstOffset,
      });
    }

    requestMobileSliderUpdate(slider) {
      if (slider.frameRequested) return;
      slider.frameRequested = true;
      window.requestAnimationFrame(() => {
        slider.frameRequested = false;
        this.updateMobileSlider(slider);
      });
    }

    updateMobileSlider(slider) {
      const currentIndex = this.getMobileSliderIndex(slider);
      slider.previous.disabled = currentIndex === 0;
      slider.next.disabled = currentIndex === this.getMobileSliderMaxIndex(slider);
      slider.status.textContent = `Karte ${currentIndex + 1} von ${slider.cards.length}`;
    }

    syncMobileSliders() {
      this.mobileSliders.forEach((slider) => {
        const mobileActive = this.mobileSliderMedia.matches && slider.cards.length > 1 && !slider.module.hidden;
        const desktopActive = slider.desktopEnabled && !this.mobileSliderMedia.matches
          && slider.cards.length > (this.desktopMedia.matches ? 3 : 2) && !slider.module.hidden;
        const active = mobileActive || desktopActive;
        slider.module.classList.toggle('is-mobile-slider', mobileActive);
        slider.module.classList.toggle('is-desktop-slider', desktopActive);
        slider.controls.hidden = !active;

        if (active) {
          slider.track.tabIndex = 0;
          slider.track.setAttribute('role', 'region');
          slider.track.setAttribute('aria-roledescription', 'Karussell');
          slider.track.setAttribute('aria-label', slider.label);
          slider.cards.forEach((card, index) => {
            card.setAttribute('role', 'group');
            card.setAttribute('aria-roledescription', 'Folie');
            card.setAttribute('aria-label', `${index + 1} von ${slider.cards.length}`);
          });
          this.requestMobileSliderUpdate(slider);
        } else {
          slider.track.removeAttribute('tabindex');
          slider.track.removeAttribute('role');
          slider.track.removeAttribute('aria-roledescription');
          slider.track.removeAttribute('aria-label');
          slider.cards.forEach((card) => {
            card.removeAttribute('role');
            card.removeAttribute('aria-roledescription');
            card.removeAttribute('aria-label');
          });
          slider.track.scrollLeft = 0;
        }
      });
    }

    initializeExternalVideos() {
      this.root.querySelectorAll('[data-article-external-video]').forEach((container) => {
        const button = container.querySelector('[data-article-video-load]');
        const fallback = container.querySelector('[data-article-video-fallback]');
        if (!button) return;

        const source = buildExternalVideoUrl(container.dataset.videoProvider, container.dataset.videoId);
        if (!source) {
          button.hidden = true;
          if (fallback) fallback.hidden = false;
          return;
        }

        const onLoad = () => this.loadExternalVideo(container, button, source);
        button.addEventListener('click', onLoad, { once: true });
        this.externalVideoTriggers.push({ button, onLoad });
      });
    }

    loadExternalVideo(container, button, source) {
      button.disabled = true;
      const iframe = document.createElement('iframe');
      iframe.className = 'oe-article-video__iframe';
      iframe.src = source;
      iframe.title = container.dataset.videoTitle || 'Externes Video';
      iframe.loading = 'lazy';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      container.replaceChildren(iframe);
      container.setAttribute('data-video-loaded', '');
      window.requestAnimationFrame(() => iframe.focus());
    }

    syncSidebarVisibility() {
      if (!this.sidebar) return;
      const hasToc = Boolean(this.toc && !this.toc.hidden);
      const hasCards = Boolean(this.sidebar.querySelector('[data-article-card]'));
      const visible = hasToc || hasCards;
      this.sidebar.hidden = !visible;
      this.root.classList.toggle('has-sidebar', visible);
    }

    observeBodyChanges() {
      if ('MutationObserver' in window) {
        this.mutationObserver = new MutationObserver(() => {
          window.clearTimeout(this.rebuildTimer);
          this.rebuildTimer = window.setTimeout(() => {
            this.removeEmptyParagraphs();
            this.enhanceTables();
            this.rebuildToc();
          }, 120);
        });
        this.mutationObserver.observe(this.body, { childList: true, subtree: true });
      }

    }

    destroy() {
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      this.desktopMedia.removeEventListener?.('change', this.onResize);
      this.mobileSliderMedia.removeEventListener?.('change', this.onResize);
      this.tocToggle?.removeEventListener('click', this.onToggle);
      this.tocList?.removeEventListener('click', this.onTocClick);
      this.observer?.disconnect();
      this.mutationObserver?.disconnect();
      window.clearTimeout(this.rebuildTimer);
      this.restoreSidebarCards();
      this.desktopSidebarCards.forEach((card) => card.removeAttribute('data-desktop-sidebar-only'));
      this.mobileSliders.forEach((slider) => {
        slider.previous.removeEventListener('click', slider.onPrevious);
        slider.next.removeEventListener('click', slider.onNext);
        slider.track.removeEventListener('scroll', slider.onScroll);
        slider.track.removeEventListener('keydown', slider.onKeydown);
        slider.module.classList.remove('oe-article-mobile-slider', 'is-mobile-slider', 'is-desktop-slider');
        slider.track.classList.remove('oe-article-mobile-slider__track');
        if (slider.controlsCreated) slider.controls.remove();
        else slider.controls.hidden = true;
      });
      this.externalVideoTriggers.forEach(({ button, onLoad }) => button.removeEventListener('click', onLoad));
      this.root.classList.remove('is-enhanced', 'has-sidebar');
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-article-redesign]').forEach((root) => {
      if (controllers.has(root)) return;
      controllers.set(root, new ArticleRedesignController(root));
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-article-redesign]').forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  });
})();
